import { Flex, ScrollArea, Stack, Toaster } from '@backyard-ui/core'
import { InvoicesType } from 'frontendMyOrders/types'
import { formatDate } from 'frontendMyOrders/utils/formatters'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Provider } from 'react-redux'

import { useUserResources } from 'scripts/react-components/shared/hooks'

import {
  setPanicError,
  resetProperties,
  setCanSendMessage,
  setProperties
} from './redux/properties'
import {
  ArchivedMessageList,
  AttachmentPreview,
  ButtonsGroup,
  Divider,
  Header,
  Notification,
  PanicError,
  SkeletonDescription,
  SkeletonFooter,
  SkeletonTitle,
  Typebar,
  NewChatButton
} from '../componentsRefact'
import { ButtonProps } from '../componentsRefact/ButtonsGroup/ButtonsGroup'
import {
  generateCancellationMessage,
  generateConfirmOtherOptionCancellationMessage,
  generateContactEmailMessage,
  generateContactMessage,
  generateContactNumberMessage,
  generateContactOnSendMessage,
  generateDefectiveProductMessage,
  generateDeliveryInformationMessage,
  generateEndContactMessage,
  generateGetContactMessage,
  generateInactiveSellerMessage,
  generateIncorrectProductMessage,
  generateInvoiceEmptyMessage,
  generateInvoiceMessage,
  generateOtherSubjectsMessage,
  generateProductInformationMessage,
  generateReversalMessage,
  generateShiftReturnMessage,
  generateSubOptionConfirmationCancellationMessage,
  generateSubOptionOthersCancellationMessage,
  generateTransferMessage,
  interactiveChatItem
} from '../dictionary'
import {
  beginMiraklFlow,
  disclaimerToUseOnlyChat,
  sellerTimeToResponse
} from '../dictionary/mirakl'
import { generateMissingProductMessage } from '../dictionary/missing-product'
import { generateSubOptionShiftReturnMessage } from '../dictionary/shift-return'
import {
  generateTernaryMessage,
  generateTernaryThruthyMessage
} from '../dictionary/ternary'
import { Chat } from './Chat'
import { SellerChat } from './SellerChat'
import { useCreateLayout, useMarketplaceChatService } from './hooks'
import {
  resetMessages,
  setMessages,
  updateTernarySelected
} from './redux/messages'
import {
  store as defaultStore,
  useAppDispatch,
  useAppSelector
} from './redux/store'
import {
  ApiChatItemType,
  OptionsType,
  OptionType,
  ShippingAddressType,
  StepsType,
  StepType,
  SubOptionsType,
  ThreadsType
} from './types'
import { deliveryInformationMessage } from './utils/delivery-information-message'
import { handleErrors } from './utils/handleErrors'
import { isValidEmail } from './utils/isValidEmail'
import { styles } from './App.styles'
import useApp from './useApp'

export type AppProps = {
  orderId: string
  marketplaceOrder: string
  shopName: string
  steps?: StepsType
  threads?: ThreadsType
  shippingAddress?: ShippingAddressType
  deliveryDate?: {
    date: string
    message: string | null
  }
  trackingCode?: string
  trackingUrl?: string
  invoices?: InvoicesType
  closeTimeInHours: number
  isLMFreight?: boolean
}

export type NativeAppProps = {
  orderId: string
  marketplaceOrder: string
  shopName?: string
  steps?: string
  threads?: string
  shippingAddress?: string
  deliveryDate?: string
  trackingCode?: string
  trackingUrl?: string
  invoices?: string
  closeTimeInHours: number
  isLMFreight?: string
}

const App = ({
  orderId,
  marketplaceOrder,
  shopName,
  steps = [],
  shippingAddress,
  deliveryDate,
  trackingCode,
  trackingUrl,
  invoices,
  threads,
  closeTimeInHours,
  isLMFreight
}: AppProps) => {
  const useStateMessage = useState('')
  const wrapperRef = useRef<HTMLDivElement>(null)
  const messagesRef = useRef<HTMLDivElement>(null)
  const [isFetched, setIsFetched] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const [buttons, setButtons] = useState<ButtonProps[]>([])
  const [currentThread, setCurrentThread] = useState<ThreadsType[0] | null>(
    null
  )

  const { getInitialChatHistoryVisibility } = useApp()
  const [isChatHistoryVisible, toggleChatHistoryVisibility] = useState(
    getInitialChatHistoryVisibility(threads)
  )

  const { post, transfer, close } = useMarketplaceChatService()
  const { initializeChatMessages, initializeChatProperties } = useCreateLayout()
  const { userResources } = useUserResources()
  const { properties, options, messages } = useAppSelector(state => state)
  const { threadId, userName, chatStatus, panicError } = properties.data
  const dispatch = useAppDispatch()

  const className = styles()

  const getMarketplaceChat = async () => {
    const response = await initializeChatProperties({
      marketplaceOrder,
      orderId,
      shopName,
      deliveryDate,
      invoices,
      shippingAddress,
      steps,
      trackingCode,
      trackingUrl,
      userEmail: userResources?.email,
      userName: userResources?.name,
      userPhone: userResources?.phone,
      threads
    })

    setIsFetched(true)

    if (response) return response
  }

  const recreateContactFlow = useCallback(
    ({
      createdAt,
      subOptions,
      subOption,
      option,
      options,
      ticket,
      userResourcesEmail,
      userResourcesPhone
    }: {
      createdAt: string
      subOptions: SubOptionsType
      subOption: string
      option: string
      options: OptionsType
      ticket: {
        contact: string
        isOriginal: boolean
        type: string
        protocol: string
      }
      userResourcesEmail: string
      userResourcesPhone: string
    }) => {
      if (option === 'cancellation') {
        dispatch(
          setMessages(generateCancellationMessage(createdAt, subOptions))
        )
      }

      dispatch(updateTernarySelected('SIM'))

      dispatch(
        setProperties({
          contactSelected: ticket.contact,
          contactType: ticket.type,
          contactIsOriginal: ticket.isOriginal
        })
      )

      dispatch(updateTernarySelected(ticket.isOriginal ? 'SIM' : 'NAO'))

      if (option === 'cancellation') {
        if (subOption === 'other') {
          dispatch(
            setMessages(
              generateSubOptionOthersCancellationMessage({ createdAt })
            )
          )

          dispatch(
            setMessages(
              interactiveChatItem({
                attachments: [],
                date: createdAt,
                from: 'user',
                message: subOption
              })
            )
          )

          dispatch(
            setMessages(
              generateConfirmOtherOptionCancellationMessage({
                createdAt,
                placeholder: subOption
              })
            )
          )
        } else {
          dispatch(
            setMessages(
              generateSubOptionConfirmationCancellationMessage({
                createdAt,
                subOption,
                option,
                options
              })
            )
          )

          dispatch(updateTernarySelected('SIM'))
        }
      }

      dispatch(setMessages(generateContactMessage({ createdAt })))

      if (ticket.type === 'email') {
        dispatch(
          setMessages(
            generateContactEmailMessage({
              createdAt,
              email: userResourcesEmail
            })
          )
        )
      } else {
        dispatch(
          setMessages(
            generateContactNumberMessage({
              createdAt,
              phoneNumber: userResourcesPhone,
              type: ticket.type === 'phone' ? 'phone' : 'whatsapp'
            })
          )
        )
      }

      dispatch(updateTernarySelected(ticket.isOriginal ? 'SIM' : 'NAO'))

      if (!ticket.isOriginal) {
        dispatch(
          setMessages(
            generateGetContactMessage({ createdAt, contactType: ticket.type })
          )
        )

        dispatch(
          setMessages(
            interactiveChatItem({
              attachments: [],
              date: createdAt,
              from: 'user',
              message: ticket.contact
            })
          )
        )
      }

      dispatch(
        setMessages(
          generateEndContactMessage({
            contact: ticket.contact,
            contactType: ticket.type,
            createdAt,
            protocol: ticket.protocol
          })
        )
      )
    },
    [dispatch]
  )

  const updateLayoutChat = useCallback(() => {
    const {
      option,
      subOption,
      createdAt,
      sellerResponseTimeInHours,
      threadId,
      shippingAddressFormatted,
      apiChat,
      ticket,
      userResourcesEmail,
      userResourcesPhone,
      type
    } = properties.data

    const subOptions = options.data.find(
      currentOption => currentOption.id === option
    )?.subOptions

    if (option === 'reversal') {
      dispatch(
        setMessages(
          generateReversalMessage({
            createdAt: createdAt
          })
        )
      )

      if (type === 'ticket') {
        recreateContactFlow({
          createdAt,
          subOptions: subOptions ?? [],
          subOption: subOption ?? '',
          options: options.data,
          option: option ?? '',
          ticket,
          userResourcesEmail,
          userResourcesPhone
        })
      }
    }

    if (option === 'invoice') {
      if (invoices) {
        const accessKey = invoices[0]?.accessKey ?? ''

        dispatch(
          setMessages(
            generateInvoiceMessage({
              createdAt: createdAt,
              accessKey
            })
          )
        )
      } else {
        dispatch(
          setMessages(
            generateInvoiceEmptyMessage({
              createdAt: createdAt
            })
          )
        )
      }

      dispatch(
        setMessages(
          generateTernaryMessage({
            createdAt: createdAt
          })
        )
      )

      if (
        currentThread?.status === 'notify' ||
        properties.data.chatStatus === 'notify'
      ) {
        dispatch(updateTernarySelected('SIM'))

        dispatch(setMessages(generateTernaryThruthyMessage({ createdAt })))
      } else if (threadId) {
        dispatch(updateTernarySelected('NAO'))

        dispatch(
          setMessages(
            beginMiraklFlow({
              shopName: shopName,
              createdAt: createdAt
            })
          )
        )
      }
    }

    if (option === 'deliveryInformation') {
      const deliveryInformationStep = steps?.find(
        (step: StepType) => step.current
      )

      const message = deliveryInformationMessage({
        slug: deliveryInformationStep?.slug ?? '',
        shippingAddressFormatted: shippingAddressFormatted ?? '',
        date: deliveryInformationStep?.date ?? '',
        deliveryDate: deliveryDate?.date ?? '',
        trackingCode: trackingCode ?? '',
        trackingUrl: trackingUrl ?? '',
        description: deliveryInformationStep?.description ?? ''
      })

      dispatch(
        setMessages(
          generateDeliveryInformationMessage({
            message,
            createdAt: createdAt
          })
        )
      )

      dispatch(
        setMessages(
          generateTernaryMessage({
            createdAt: createdAt
          })
        )
      )

      if (
        currentThread?.status === 'notify' ||
        properties.data.chatStatus === 'notify'
      ) {
        dispatch(updateTernarySelected('SIM'))

        dispatch(setMessages(generateTernaryThruthyMessage({ createdAt })))
      } else if (threadId) {
        dispatch(updateTernarySelected('NAO'))

        dispatch(
          setMessages(
            beginMiraklFlow({
              shopName: shopName,
              createdAt: createdAt
            })
          )
        )
      }
    }

    if (option === 'defectiveProduct') {
      dispatch(
        setMessages(
          generateDefectiveProductMessage({
            shopName: shopName,
            createdAt: createdAt
          })
        )
      )
    }

    if (option === 'incorrectProduct') {
      dispatch(
        setMessages(
          generateIncorrectProductMessage({
            shopName: shopName,
            createdAt: createdAt
          })
        )
      )
    }

    if (option === 'cancellation') {
      if (type === 'ticket') {
        recreateContactFlow({
          createdAt,
          subOptions: subOptions ?? [],
          subOption: subOption ?? '',
          options: options.data,
          option: option ?? '',
          ticket,
          userResourcesEmail,
          userResourcesPhone
        })
      } else {
        dispatch(
          setMessages(generateCancellationMessage(createdAt, subOptions))
        )

        dispatch(
          setMessages(
            beginMiraklFlow({
              shopName: shopName,
              createdAt: createdAt
            })
          )
        )
      }
    }

    if (option === 'shiftReturn') {
      dispatch(setMessages(generateShiftReturnMessage(createdAt, subOptions)))

      dispatch(
        setMessages(
          generateSubOptionShiftReturnMessage({
            shopName: shopName,
            createdAt: createdAt
          })
        )
      )
    }

    if (option === 'productInformation') {
      dispatch(
        setMessages(
          generateProductInformationMessage({
            shopName: shopName,
            createdAt: createdAt
          })
        )
      )
    }

    if (option === 'missingProduct') {
      dispatch(
        setMessages(
          generateMissingProductMessage({
            shopName: shopName,
            createdAt: createdAt
          })
        )
      )
    }

    if (option === 'otherSubject') {
      dispatch(
        setMessages(
          generateOtherSubjectsMessage({
            createdAt: createdAt
          })
        )
      )
    }

    apiChat.forEach((chatItem: ApiChatItemType, index) => {
      dispatch(setMessages(interactiveChatItem(chatItem)))

      if (index === 0) {
        if (type === 'chat') {
          dispatch(
            setMessages(
              sellerTimeToResponse({
                shopName,
                createdAt,
                sellerResponseTimeInHours
              })
            )
          )
          dispatch(setMessages(disclaimerToUseOnlyChat({ createdAt })))
        }
      }
    })

    if (properties.data.chatStatus === 'inactive-seller') {
      dispatch(setMessages(generateInactiveSellerMessage()))
    }

    if (properties.data.chatStatus === 'transfer') {
      dispatch(setMessages(generateInactiveSellerMessage(createdAt)))
      dispatch(
        setMessages(
          interactiveChatItem({
            attachments: [],
            date: createdAt,
            from: 'bot',
            message:
              'Foi solicitado <strong>contato com a Leroy Merlin</strong>.'
          })
        )
      )
      dispatch(
        setMessages(
          generateEndContactMessage({
            contact: ticket.contact,
            contactType: ticket.type,
            createdAt: createdAt,
            recreated: true,
            protocol: ticket.protocol
          })
        )
      )
    }
  }, [
    properties.data,
    options.data,
    currentThread,
    invoices,
    dispatch,
    deliveryDate?.date,
    shopName,
    steps,
    trackingCode,
    trackingUrl,
    recreateContactFlow
  ])

  const generateContactFlow = ({
    message,
    createdAt
  }: {
    message: string
    createdAt: string
  }) => {
    dispatch(
      setProperties({
        contactSelected: message,
        contactIsOriginal: false
      })
    )

    dispatch(
      setMessages(
        generateContactOnSendMessage({
          createdAt,
          message
        })
      )
    )
  }

  async function onSend(message: string) {
    const {
      option,
      threadId,
      subOption,
      createdAt,
      sellerResponseTimeInHours,
      contactType,
      contactIsOriginal,
      type: typeRedux
    } = properties.data
    const type = threadId
      ? typeRedux
      : options.data.find(current => current.id === option)?.type

    if (!type || !option) return

    const isTransfer = type === 'chat' && contactType

    if (isTransfer) {
      if (!isValidEmail(message) && contactType === 'email') {
        dispatch(
          setMessages(
            interactiveChatItem({
              message: 'Digite um E-mail válido.',
              from: 'bot',
              date: '',
              attachments: []
            })
          )
        )

        return
      }

      const response = await transfer({
        threadId,
        data: {
          contact: message,
          contactIsOriginal: 0,
          contactType: contactType
        },
        orderId,
        marketplaceOrder
      })

      if (response?.id) {
        dispatch(
          setProperties({
            threadId: response?.id
          })
        )

        generateContactFlow({
          message,
          createdAt
        })

        dispatch(
          setProperties({
            chatStatus: 'transfer'
          })
        )

        dispatch(
          setMessages(
            generateEndContactMessage({
              createdAt,
              contactType: contactType ?? '',
              contact: message,
              protocol: response?.ticket?.protocol
            })
          )
        )

        return
      }

      return
    }

    if (option === 'cancellation') {
      if (type === 'ticket') {
        if (!isValidEmail(message) && contactType === 'email') {
          dispatch(
            setMessages(
              interactiveChatItem({
                message: 'Digite um E-mail válido.',
                from: 'bot',
                date: '',
                attachments: []
              })
            )
          )

          return
        }

        if (contactType) {
          generateContactFlow({
            message,
            createdAt
          })
        } else {
          dispatch(
            setMessages(
              interactiveChatItem({
                message,
                from: 'user',
                date: '',
                attachments: []
              })
            )
          )

          dispatch(
            setMessages(
              generateConfirmOtherOptionCancellationMessage({
                createdAt,
                placeholder: message
              })
            )
          )

          return
        }
      }
    }

    if (option === 'reversal') {
      if (contactType) {
        if (!isValidEmail(message) && contactType === 'email') {
          dispatch(
            setMessages(
              interactiveChatItem({
                message: 'Digite um E-mail válido.',
                from: 'bot',
                date: '',
                attachments: []
              })
            )
          )

          return
        }

        generateContactFlow({
          message,
          createdAt
        })
      }
    }

    if (type === 'ticket') {
      if (contactType) {
        const response = await post(
          threadId,
          {
            type,
            option,
            subOption,
            contact: message,
            contactIsOriginal: contactIsOriginal ? 1 : 0,
            contactType: contactType
          },
          orderId,
          marketplaceOrder
        )

        if (response?.id) {
          dispatch(
            setProperties({
              threadId: response?.id,
              type: 'ticket'
            })
          )

          dispatch(
            setMessages(
              generateEndContactMessage({
                createdAt,
                contactType: contactType ?? '',
                contact: message,
                protocol: response?.ticket?.protocol
              })
            )
          )
        }
      }
    }

    if (type === 'chat') {
      const response = await post(
        threadId,
        {
          message,
          option,
          subOption,
          type,
          files
        },
        orderId,
        marketplaceOrder
      )

      if (response?.id) {
        dispatch(setMessages(interactiveChatItem(response.chat)))

        if (!threadId) {
          dispatch(
            setMessages(
              sellerTimeToResponse({
                shopName,
                sellerResponseTimeInHours
              })
            )
          )
          dispatch(setMessages(disclaimerToUseOnlyChat({ createdAt })))

          dispatch(
            setProperties({
              threadId: response.id,
              chatStatus: 'send-client',
              type: 'chat'
            })
          )
        }
      }

      setFiles([])
    }
  }

  function onUpload(file: File) {
    setFiles([file, ...files])
  }

  function onRemoveFile(index: number) {
    const newFiles = files.filter((_, i) => i !== index)
    setFiles(newFiles)
  }

  async function onSelectButtons(typeButton: string) {
    switch (typeButton) {
      case 'restart':
        window.location.reload()
        break
      case 'close':
        await close(orderId, marketplaceOrder, threadId)
        dispatch(resetProperties())
        window.location.reload()
        break
      case 'transfer':
        dispatch(setMessages(generateTransferMessage()))
        break
      default:
    }
  }

  const handleInitChat = async (id = '') => {
    setIsFetched(false)
    dispatch(resetMessages())

    await initializeChatProperties({
      marketplaceOrder,
      orderId,
      shopName,
      deliveryDate,
      invoices,
      shippingAddress,
      steps,
      trackingCode,
      trackingUrl,
      userEmail: userResources?.email,
      userName: userResources?.name,
      userPhone: userResources?.phone,
      threads,
      threadId: id
    })

    setIsFetched(true)
    toggleChatHistoryVisibility(false)
    initializeChatMessages()
    updateLayoutChat()
  }

  const handleStartNewConversation = async () => {
    setIsFetched(false)
    dispatch(resetMessages())
    dispatch(resetProperties())

    await initializeChatProperties({
      marketplaceOrder,
      orderId,
      shopName,
      deliveryDate,
      invoices,
      shippingAddress,
      steps,
      trackingCode,
      trackingUrl,
      userEmail: userResources?.email,
      userName: userResources?.name,
      userPhone: userResources?.phone
    })

    setIsFetched(true)
    toggleChatHistoryVisibility(false)
    initializeChatMessages(true)
  }

  async function onSelectArchivedMessage(id: string) {
    handleInitChat(id)
  }

  function handleNavigateBackwards() {
    const hasMoreThanOneThread = threads && threads.length > 1

    if (!hasMoreThanOneThread || isChatHistoryVisible) {
      window.location.href = '/meus-pedidos'
      return
    }

    dispatch(resetProperties())
    toggleChatHistoryVisibility(true)
  }

  const archivedMessageList =
    threads?.map(thread => {
      const optionFinded = options?.data.find(
        (option: OptionType) => thread.option === option.id
      )
      const title = optionFinded ? optionFinded.name : ''
      return {
        date: thread.createdAt
          ? formatDate(thread.createdAt, 'long_weekday_day_month')
          : '',
        id: thread.threadId,
        title,
        option: thread.option,
        isOpenThread: thread.isOpenThread,
        hasNewMessage: thread.hasNewMessage
      }
    }) || []

  useEffect(() => {
    if (wrapperRef.current && messagesRef.current) {
      if (messages.data.list.length === 2) {
        messagesRef.current.scrollIntoView({
          block: 'start',
          behavior: 'smooth'
        })
        wrapperRef.current.scrollIntoView({
          block: 'start',
          behavior: 'smooth'
        })
      } else {
        messagesRef.current.scrollIntoView({
          block: 'end',
          behavior: 'smooth'
        })
        wrapperRef.current.scrollIntoView({
          block: 'end',
          behavior: 'smooth'
        })
      }
    }

    if (
      properties.data.chatStatus === 'idle' &&
      messages?.data?.list[messages?.data?.list.length - 2]?.from === 'user'
    ) {
      dispatch(setCanSendMessage(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages])

  useEffect(
    () => {
      dispatch(
        setProperties({
          canSendMessage: false
        })
      )

      if (messages.data.list.length <= 2 && !isChatHistoryVisible) {
        if (threadId) {
          updateLayoutChat()
        }
      }

      const currentButtons: ButtonProps[] = []
      const isAMiraklChat =
        threadId &&
        ['send-client', 'send-seller', 'inactive-seller'].includes(chatStatus)

      if (!isNaN(new Date(properties?.data?.createdAt).getTime())) {
        const timeToCloseInMilils = 60 * 60 * (closeTimeInHours ?? 0) * 1000
        const createdAtTimestamp = new Date(properties.data.createdAt).getTime()

        if (
          isAMiraklChat &&
          createdAtTimestamp + timeToCloseInMilils < Date.now()
        ) {
          currentButtons.push({
            key: 'close',
            label: 'Encerrar conversa',
            isDisabled: properties.data.isLoading,
            confirmOnActionMessage:
              'Ao encerrar, consideramos que <strong>seu problema foi resolvido</strong> e não será possível enviar novas mensagens nesta conversa. Você pode consultar essa conversa na aba Conversas encerradas.'
          })
        }
      }

      if (chatStatus === 'inactive-seller') {
        currentButtons.push({
          key: 'transfer',
          label: 'Chamar Leroy Merlin',
          isDisabled: properties.data.isLoading
        })
      }

      if (!threadId) {
        currentButtons.push({
          key: 'restart',
          label: 'Recomeçar conversa',
          isDisabled: properties.data.isLoading
        })
      }

      if (isAMiraklChat) {
        dispatch(
          setProperties({
            canSendMessage: true
          })
        )
      }

      setButtons(currentButtons)
    },
    // threadId: to trigger when threadApi is called
    // userName: to trigger when userResources is called
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [threadId, userName, chatStatus]
  )

  useEffect(() => {
    if (userResources?.name && !isFetched) {
      const getData = async () => {
        const response = await getMarketplaceChat()

        if (response && !isChatHistoryVisible) {
          initializeChatMessages(false, response, {
            userName: userResources?.name ?? '',
            shopName,
            createdAt: response.createdAt
          })
        }
      }

      getData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userResources?.name])

  useEffect(() => {
    setCurrentThread(
      properties.data?.threads?.find(thread => thread.threadId === threadId) ||
        null
    )
  }, [properties.data?.threads, threadId])

  if (!!panicError && handleErrors(panicError, dispatch, setPanicError)) {
    return <PanicError />
  }

  if (!isFetched) {
    return (
      <Stack
        direction="column"
        className={className.wrapper()}
        data-testid="chat-skeleton">
        <div className={className.header()}>
          <SkeletonTitle />
        </div>
        <SkeletonDescription />
        <div className={className.footer()}>
          <SkeletonFooter />
        </div>
      </Stack>
    )
  }

  return (
    <>
      <Toaster position="top-center" gutter={8} />

      <Flex
        direction="column"
        className={className.wrapper()}
        ref={wrapperRef}
        data-testid="chat-container">
        <Stack
          direction="column"
          spacing={4}
          align="flex-start"
          className={className.header()}>
          <Stack
            direction="row"
            align="center"
            className="w-full justify-between">
            <Header
              shopName={shopName}
              isLMFreight={isLMFreight}
              onBackward={handleNavigateBackwards}
            />
            <NewChatButton
              onClick={handleStartNewConversation}
              threads={threads ?? []}
            />
          </Stack>
          <Notification />
        </Stack>

        {isChatHistoryVisible && threads ? (
          <Stack
            direction="column"
            className="flex-1"
            data-testid="archived-messages-list">
            <ArchivedMessageList
              list={archivedMessageList}
              onClick={onSelectArchivedMessage}
            />
          </Stack>
        ) : (
          <>
            <ScrollArea UNSAFE_className="px-2 lg:px-8 grow">
              <div ref={messagesRef}>
                {currentThread?.isActiveSeller ? <SellerChat /> : <Chat />}
              </div>
            </ScrollArea>

            <div className={className.footer()}>
              <Divider />
              {buttons.length > 0 && (
                <Stack spacing={4} className="bg-gray-100 py-4 px-2 lg:px-8">
                  <ButtonsGroup onClick={onSelectButtons} buttons={buttons} />
                </Stack>
              )}
              <AttachmentPreview files={files} onRemoveFile={onRemoveFile} />
              <Typebar
                useStateMessage={useStateMessage}
                onSendMessage={onSend}
                placeholder="Escreva uma mensagem"
                onUploadFile={onUpload}
                isDisabled={!properties.data.canSendMessage}
                isLoading={properties.data.isLoading}
              />
            </div>
          </>
        )}
      </Flex>
    </>
  )
}

const AppWithStore = ({
  orderId,
  marketplaceOrder,
  shopName,
  steps,
  threads,
  shippingAddress,
  deliveryDate,
  trackingCode,
  trackingUrl,
  invoices,
  store = defaultStore,
  closeTimeInHours,
  isLMFreight
}: AppProps & { store?: typeof defaultStore }) => (
  <Provider store={store}>
    <App
      orderId={orderId}
      marketplaceOrder={marketplaceOrder}
      shopName={shopName}
      steps={steps}
      threads={threads}
      shippingAddress={shippingAddress}
      deliveryDate={deliveryDate}
      trackingCode={trackingCode}
      trackingUrl={trackingUrl}
      invoices={invoices}
      closeTimeInHours={closeTimeInHours}
      isLMFreight={isLMFreight}
    />
  </Provider>
)

export default AppWithStore
